import type { MovementRepository } from '@inventory/core/domain/interfaces/MovementRepository';
import { MovementEntity } from '@inventory/core/domain/entities/Movement';
import { MovementTypes, MovementStatusTypes } from '@inventory/core/domain/shared/types';
import { BasePouchDBAdapter } from './BasePouchDBAdapter';
import type { BasePouchDocument } from '../types';

interface MovementDocument extends BasePouchDocument {
  movementType: string;
  productId: string;
  warehouseId: string;
  quantity: number;
  quantityUnit: string;
  unitCost: number;
  unitCostCurrency: string;
  unitPrice?: number;
  unitPriceCurrency?: string;
  status: string;
  reason?: string;
  initiatedBy: string;
  approvedBy?: string;
  completedBy?: string;
  cancelledBy?: string;
  failedBy?: string;
  fromLocationId?: string;
  toLocationId?: string;
  reference?: {
    type: 'PO' | 'SO' | 'TO' | 'ADJ';
    number: string;
    externalId?: string;
  };
  notes?: string;
  destinationWarehouseId?: string;
}

export class MovementPouchDBAdapter extends BasePouchDBAdapter<MovementEntity> implements MovementRepository {
  constructor(dbName: string) {
    super(dbName, 'movement');
    this.createIndices();
  }

  private async createIndices(): Promise<void> {
    await this.createIndex(['stockId']);
    await this.createIndex(['fromLocationId']);
    await this.createIndex(['toLocationId']);
    await this.createIndex(['status']);
    await this.createIndex(['type']);
  }

  protected toDocument(movement: MovementEntity): MovementDocument {
    return {
      _id: movement._id,
      type: 'movement',
      version: movement.version,
      movementType: movement.movementType,
      productId: movement.productId,
      warehouseId: movement.warehouseId,
      quantity: movement.quantity.value,
      quantityUnit: movement.quantity.unit,
      unitCost: movement.unitCost.amount,
      unitCostCurrency: movement.unitCost.currency,
      unitPrice: movement.unitPrice?.amount,
      unitPriceCurrency: movement.unitPrice?.currency,
      status: movement.status,
      reason: movement.reason,
      initiatedBy: movement.initiatedBy,
      approvedBy: movement.approvedBy,
      completedBy: movement.completedBy,
      cancelledBy: movement.cancelledBy,
      failedBy: movement.failedBy,
      fromLocationId: movement.fromLocationId,
      toLocationId: movement.toLocationId,
      reference: movement.reference,
      notes: movement.notes,
      destinationWarehouseId: movement.destinationWarehouseId,
      createdAt: movement.createdAt.toISOString(),
      updatedAt: movement.updatedAt.toISOString(),
    };
  }

  protected fromDocument(doc: MovementDocument): MovementEntity {
    return MovementEntity.restore({
      _id: doc._id,
      type: doc.type,
      version: doc.version ?? 1,
      movementType: doc.movementType as any,
      productId: doc.productId,
      warehouseId: doc.warehouseId,
      quantity: { value: doc.quantity ?? 0, unit: doc.quantityUnit ?? 'units' },
      unitCost: { amount: doc.unitCost, currency: doc.unitCostCurrency ?? 'USD' },
      unitPrice: doc.unitPrice !== undefined ? { amount: doc.unitPrice, currency: doc.unitPriceCurrency ?? 'USD' } : undefined,
      status: doc.status as any,
      reason: doc.reason,
      initiatedBy: doc.initiatedBy,
      approvedBy: doc.approvedBy,
      completedBy: doc.completedBy,
      cancelledBy: doc.cancelledBy,
      failedBy: doc.failedBy,
      fromLocationId: doc.fromLocationId,
      toLocationId: doc.toLocationId,
      reference: doc.reference,
      notes: doc.notes,
      destinationWarehouseId: doc.destinationWarehouseId,
      createdAt: new Date(doc.createdAt),
      updatedAt: new Date(doc.updatedAt),
    });
  }

  // protected fromDocument(doc: MovementDocument): MovementEntity {
  //   return new MovementEntity({
  //     _id: doc._id,
  //     type: doc.type,
  //     version: doc.version,
  //     movementType: doc.movementType,
  //     productId: doc.productId,
  //     warehouseId: doc.warehouseId,
  //     quantity: { value: doc.quantity, unit: 'units' },
  //     unitCost: { amount: doc.unitCost, currency: 'USD' },
  //     createdAt: new Date(doc.createdAt),
  //     updatedAt: new Date(doc.updatedAt),
  //   });
  // }

  // async findByLocation(locationId: string): Promise<MovementEntity[]> {
  //   const fromResults = await this.query({ fromLocationId: locationId });
  //   const toResults = await this.query({ toLocationId: locationId });
  //   return [...fromResults, ...toResults];
  // }

  // async findByStock(stockId: string): Promise<MovementEntity[]> {
  //   return this.query({ stockId });
  // }

  async findByType(type: keyof typeof MovementTypes): Promise<MovementEntity[]> {
    const result = await this.db.find({
      selector: { type: 'movement', movementType: type }
    });
    return result.docs.map(doc => this.fromDocument(doc as MovementDocument));
  }

  async findByStatus(status: keyof typeof MovementStatusTypes): Promise<MovementEntity[]> {
    const result = await this.db.find({
      selector: { type: 'movement', status: status }
    });
    return result.docs.map(doc => this.fromDocument(doc as MovementDocument));
  }

  async findByReference(referenceNumber: string): Promise<MovementEntity[]> {
    const result = await this.db.find({
      selector: { type: 'movement', 'reference.number': referenceNumber },
    });
    return result.docs.map(doc => this.fromDocument(doc as MovementDocument));
  }

  async findByProductId(productId: string): Promise<MovementEntity[]> {
    const result = await this.db.find({
      selector: { type: 'movement', productId: productId }
    });
    return result.docs.map(doc => this.fromDocument(doc as MovementDocument));
  }

  // async findByWarehouse(warehouseId: string): Promise<MovementEntity[]> {
  //   // We need to search for movements that have fromLocationId or toLocationId
  //   // in any location within this warehouse
  //   return this.findAll().then(movements =>
  //     movements.filter(movement =>
  //     (movement.fromLocationId?.startsWith(warehouseId) ||
  //       movement.toLocationId?.startsWith(warehouseId))
  //     )
  //   );
  // }

  // async findByDateRange(startDate: Date, endDate: Date): Promise<MovementEntity[]> {
  //   const result = await this.db.find({
  //     selector: {
  //       type: 'movement',
  //       createdAt: {
  //         $gte: startDate.toISOString(),
  //         $lte: endDate.toISOString()
  //       }
  //     }
  //   });
  //   return result.docs.map(doc => this.fromDocument(doc as MovementDocument));
  // }

  // async create(movement: MovementEntity): Promise<MovementEntity> {
  //   const doc = this.toDocument(movement);
  //   try {
  //     await this.db.put(doc);
  //     return movement;
  //   } catch (error) {
  //     console.error('Error creating movement:', error);
  //     throw error;
  //   }
  // }

  // async update(movement: MovementEntity): Promise<MovementEntity> {
  //   try {
  //     const existing = await this.db.get(movement.id);
  //     const doc = this.toDocument(movement);
  //     await this.db.put({ ...doc, _rev: existing._rev });
  //     return movement;
  //   } catch (error) {
  //     console.error('Error updating movement:', error);
  //     throw error;
  //   }
  // }

  async save(entity: MovementEntity): Promise<void> {
    const doc = this.toDocument(entity);
    try {
      const existing = await this.db.get(entity._id);
      await this.db.put({ ...doc, _rev: (existing as any)._rev });
    } catch (error) {
      if ((error as any).status === 404) {
        await this.db.put(doc);
      } else {
        throw this.handleError(error as any);
      }
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const doc = await this.db.get(id);
      await this.db.remove(doc);
    } catch (error) {
      if ((error as any).name === 'not_found') {
        return;
      }
      throw error;
    }
  }

  async bulkSave(entities: MovementEntity[]): Promise<void> {
    const docs = entities.map(entity => this.toDocument(entity));
    const results = await this.db.bulkDocs(docs);
    const errors = results.filter((r: any) => r.error);
    if (errors.length > 0) {
      throw new Error(`Bulk save failed for ${errors.length} movements`);
    }
  }
}